/**
 * Your account (v2 frame "Hub · Your account"), the hub page every app links to.
 *
 * Appearance (frame 92:292, approved 1 Nov) is one card: Mode (System · Light · Dark, as in the
 * account panel since 2 Nov), Tint (a switch:
 * the surfaces take the accent's colour, in any mode; it was the fourth mode, Colored) with its
 * strength, Subtle or Strong (frame 94:292, 2 Nov), and the
 * accent, one row of six presets and Custom with the one in use ringed, then a Preview strip. All
 * are stored on the hub account and every CX app applies them with the session; each applies the
 * moment you pick it (no Save), so the card and the strip repaint as you choose: the control is its
 * own preview. A custom colour that cannot keep 4.5:1 (in Light, Dark and both tints) is snapped to
 * the nearest shade of the same hue that can, and the picker says so. Password is a plain group; Change password is the
 * page's one primary. Sign out is in the account menu (sidebar foot; the avatar on phones), not here.
 *
 * The account menu's "Your account" opens cx-ui's AccountPanel over the page instead (frame
 * 94:292); this page stays for the password, which the panel links to, and as the full view.
 * The accent picker is cx-ui's (moved there in 0.3.7 so every app's panel draws the same one).
 *
 * "Where you are signed in" is drawn in the frame and waits for sessions to record their device
 * (design build note, 30 Oct).
 */
import { useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  ACCENT_PRESETS,
  APPEARANCES,
  AccentPicker,
  Field,
  Group,
  Headline,
  Segmented,
  TINT_STRENGTHS,
  applyPreferences,
  resolveAccent,
  useToast,
  type Appearance,
  type StoredPreferences,
  type TintStrength,
} from '@buckmason/cx-ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTitle } from '../lib/useTitle';
import type { Me } from '../lib/types';

type Prefs = { appearance?: Appearance; tint?: boolean; tintStrength?: TintStrength; accent?: string };

export function Account() {
  useTitle('Your account');
  const { me, setMe } = useAuth();
  const toast = useToast();

  // What the screen shows now, including picks whose save has not answered yet, and a number per
  // save so only the latest reply updates the account: quick picks (Dark, then an accent) can
  // answer out of order, and an older reply must not undo a newer pick.
  const shown = useRef<Prefs>({});
  const latest = useRef(0);
  const savePrefs = useMutation({
    mutationFn: (v: { body: Prefs; seq: number }) => api.post<{ person: Me }>('/api/id/auth/preferences', v.body),
    onMutate: ({ body }) => {
      shown.current = {
        appearance: body.appearance ?? shown.current.appearance ?? me?.appearance,
        tint: body.tint ?? shown.current.tint ?? me?.tint,
        tintStrength: body.tintStrength ?? shown.current.tintStrength ?? me?.tintStrength,
        accent: body.accent ?? shown.current.accent ?? me?.accent,
      };
      applyPreferences(shown.current as StoredPreferences);
    },
    onSuccess: (r, { seq }) => {
      if (seq !== latest.current) return;
      shown.current = {};
      setMe(r.person);
    },
    onError: (_e, { seq }) => {
      if (seq !== latest.current) return;
      // Put the screen back to what the account holds.
      shown.current = {};
      applyPreferences({ appearance: me?.appearance, tint: me?.tint, tintStrength: me?.tintStrength, accent: me?.accent });
      toast('That did not save. Try again.');
    },
  });
  const save = (body: Prefs) => savePrefs.mutate({ body, seq: ++latest.current });

  if (!me) return null;

  const roles = [me.hubRoleName === 'Hub admin' ? 'Hub admin' : null, ...me.apps.map((a) => `${a.roleName} in ${a.name}`)].filter(Boolean);
  // 'auto' shows as the preset it stands for (resolveAccent is the one place that knows which).
  const resolved = resolveAccent(me.accent, me.appearance);
  const accentIsPreset = 'preset' in resolved;
  const shownAccent = 'preset' in resolved ? resolved.preset : me.accent;
  const presetName = accentIsPreset ? ACCENT_PRESETS.find((p) => p.key === shownAccent)?.name : 'Your colour';
  const tint = !!me.tint;

  return (
    <div className="page arrive">
      <Headline title="Your account">{[me.email, ...roles].join(' · ')}</Headline>

      <Group heading="Appearance">
        <div className="account-field">
          <span className="account-label" id="mode-label">
            Mode
          </span>
          <Segmented
            label="Mode"
            value={me.appearance}
            options={APPEARANCES}
            onChange={(v) => save({ appearance: v })}
          />
        </div>
        <div className="account-tint">
          <span className="account-label" id="tint-label">
            Tint
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={tint}
            aria-labelledby="tint-label"
            className={`account-switch${tint ? ' is-on' : ''}`}
            onClick={() => save({ tint: !tint })}
          >
            <span className="account-switch-knob" aria-hidden="true" />
          </button>
        </div>
        {tint && (
          <div className="account-field">
            <span className="account-label">Tint strength</span>
            <Segmented label="Tint strength" value={me.tintStrength ?? 'subtle'} options={TINT_STRENGTHS} onChange={(v) => save({ tintStrength: v })} />
          </div>
        )}
        <div className="account-field">
          <span className="account-label">
            Accent <span className="account-accent-name">{presetName}</span>
          </span>
          <AccentPicker value={shownAccent} isPreset={accentIsPreset} onPick={(accent) => save({ accent })} />
        </div>
        <div className="account-preview" aria-hidden="true">
          <span className="account-label">Preview</span>
          <div className="account-preview-card">
            <span className="account-preview-text">
              <span className="account-preview-title">Returns quiz</span>
              <span className="meta">Five questions · pass at 80%</span>
            </span>
            <span className="account-preview-link">Preview</span>
            <span className="account-preview-btn">Save</span>
          </div>
        </div>
      </Group>

      <PasswordGroup />

    </div>
  );
}

function PasswordGroup() {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const change = useMutation({
    mutationFn: () => api.post<{ message: string }>('/api/id/auth/change-password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setError(null);
      toast('Password changed. Other devices are signed out.');
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'That did not work.'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    change.mutate();
  };

  return (
    <Group heading="Password">
      <form className="account-password" onSubmit={submit}>
        <Field label="Current" error={error ?? undefined}>
          <input type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="New">
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            placeholder="At least 12 characters"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <button type="submit" className="primary" disabled={change.isPending}>
          {change.isPending ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </Group>
  );
}
